/**
 * Dispense volume + atomizer selector — Intoxication EU
 */

const BOTTLE_PRICES = {1:35, 2:35, 3:35, 5:35, 8:35, 10:40, 15:40, 20:55, 30:45};
const ATOMIZER_VOLUMES = new Set([5, 10, 15, 20, 30]);

class DhDispenseSelector extends HTMLElement {
  connectedCallback() {
    this.pricePerMl = parseFloat(this.dataset.pricePerMl) || 0;
    this.stockMl = parseInt(this.dataset.stockMl, 10) || 0;
    this.ppmFormatted = this.dataset.ppmFormatted || '';
    this.catalog = this._parseCatalog();
    this.vesselsEnabled = !!(this.catalog && this.catalog.enabled);
    this.selectedVessel = null;

    this.pills = this.querySelectorAll('.dh-dispense__pill');
    this.currentVolumeEl = this.querySelector('.dh-dispense__current-volume');
    this.vesselsRoot = this.querySelector('.dh-dispense__vessels');

    const section = this.closest('.shopify-section') || document;
    this.section = section;
    this.productForm = section.querySelector('form[data-type="add-to-cart-form"]')
      || section.querySelector('form[action*="/cart/add"]');
    this.variantSelect = section.querySelector('select[name="id"]');
    this.kallesSwatch = section.querySelector('.t4s-swatch');
    this.kallesPrice = section.querySelector('.t4s-product-price');
    this.skuEls = section.querySelectorAll('[data-product__sku-number], .t4s-sku-value');

    if (!this.productForm || !this.variantSelect) {
      console.warn('[dh-dispense] Form or variant select not found');
      return;
    }

    this._hideKallesSwatch();
    this._hideUnbuyablePills();
    if (!this.pills.length) {
      this._renderEmptyState();
      return;
    }

    this._insertPriceBlock(section);
    this._injectPropertyInputs();
    this._renderVesselRows();
    this._bindPills();
    this._bindVariantSync();
    this._bindStickyAtc();
    this._bindAtcIntercept();

    const requestedId = new URLSearchParams(window.location.search).get('variant')
      || (this.variantSelect ? String(this.variantSelect.value) : '');
    const requestedPill = requestedId
      ? Array.from(this.pills).find((p) => String(p.dataset.variantId) === String(requestedId) && !p.hidden)
      : null;
    const activePill = requestedPill
      || this.querySelector('.dh-dispense__pill--active:not([hidden])')
      || this.pills[0];
    if (activePill) this._selectPill(activePill);
  }

  _parseCatalog() {
    const raw = this.dataset.vessels || '';
    if (!raw || raw === 'null' || raw === 'nil') return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (err) {
      return null;
    }
  }

  _hideKallesSwatch() {
    if (this.kallesSwatch) this.kallesSwatch.style.display = 'none';
  }

  _hideUnbuyablePills() {
    this.pills.forEach((pill) => {
      const vol = parseInt(pill.dataset.volume, 10);
      const qty = parseInt(pill.dataset.qty, 10);
      const perfumeLeft = Number.isNaN(qty)
        ? !(this.stockMl > 0 && this.stockMl < 9999 && vol > this.stockMl)
        : qty > 0;
      const vesselLeft = !this.vesselsEnabled || this._vesselsFor(vol).length > 0;
      if (!perfumeLeft || !vesselLeft) pill.hidden = true;
    });
    this.pills = this.querySelectorAll('.dh-dispense__pill:not([hidden])');
  }

  _renderEmptyState() {
    const header = this.querySelector('.dh-dispense__header');
    const pills = this.querySelector('.dh-dispense__pills');
    if (header) header.hidden = true;
    if (pills) pills.hidden = true;
    if (this.vesselsRoot) this.vesselsRoot.hidden = true;

    if (!this.querySelector('.dh-dispense__empty')) {
      const note = document.createElement('p');
      note.className = 'dh-dispense__empty';
      note.textContent = 'This fragrance is temporarily out of stock.';
      this.appendChild(note);
    }

    const lock = () => {
      [
        this.productForm.querySelector('button[name="add"]'),
        document.querySelector('[data-sticky-addtocart] [data-action-atc]'),
      ].forEach((btn) => {
        if (!btn) return;
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        const label = btn.querySelector('.t4s-btn-atc_text') || btn;
        label.textContent = 'Sold out';
      });
    };
    lock();
    [150, 600, 1500, 3000].forEach((ms) => window.setTimeout(lock, ms));
    if (this.variantSelect) this.variantSelect.addEventListener('change', lock);

    const block = (e) => {
      const btn = e.target.closest('button[name="add"], [data-action-atc], [data-atc-form]');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      lock();
    };
    window.addEventListener('click', block, true);
    this.productForm.addEventListener('submit', block, true);
  }

  _overrideKallesPrice(total) {
    if (!this.kallesPrice) return;
    const amount = total != null ? total : this.pricePerMl;
    if (!amount && amount !== 0) return;
    const f = this._formatPrice(amount);
    this.kallesPrice.innerHTML = '<span class="money">\u20ac' + f + '</span>';
  }

  _insertPriceBlock(section) {
    const skuWrapper = section.querySelector('.t4s-sku-wrapper');
    if (!skuWrapper || section.querySelector('.dh-price-block')) return;

    const el = document.createElement('div');
    el.className = 'dh-price-block';
    el.innerHTML =
      '<div class="dh-price-block__per-ml" hidden></div>' +
      '<div class="dh-price-block__breakdown">' +
        '<span class="dh-price-block__liquid"></span>' +
        '<span class="dh-price-block__sep">+</span>' +
        '<span class="dh-price-block__bottle"></span>' +
      '</div>';

    skuWrapper.insertAdjacentElement('afterend', el);

    this.blockPerMl = el.querySelector('.dh-price-block__per-ml');
    this.blockLiquid = el.querySelector('.dh-price-block__liquid');
    this.blockBottle = el.querySelector('.dh-price-block__bottle');
  }

  _unitPrice(pill, volume) {
    const variantPrice = parseFloat(pill && pill.dataset.price);
    if (volume > 0 && !Number.isNaN(variantPrice) && variantPrice > 0) {
      return variantPrice / volume;
    }
    return this.pricePerMl > 0 ? this.pricePerMl : 0;
  }

  _updatePriceBlock(volume, liquidCost, bottleCost, unitPrice) {
    const money = this.dataset.money || '';
    const perfume = this.dataset.perfume || 'Perfume';
    const bottle = this.dataset.bottle || 'Bottle';
    const perMl = this.dataset.perMl || 'Price per 1 ml';
    const suffix = money ? ' ' + money : '';
    if (this.blockPerMl) {
      const unit = unitPrice > 0 ? unitPrice : 0;
      if (unit > 0) {
        this.blockPerMl.hidden = false;
        this.blockPerMl.textContent = perMl + ' — ' + this._formatUnit(unit) + suffix;
      } else {
        this.blockPerMl.hidden = true;
        this.blockPerMl.textContent = '';
      }
    }
    if (this.blockLiquid) {
      this.blockLiquid.textContent = perfume + ': ' + this._formatPrice(liquidCost) + suffix;
    }
    if (this.blockBottle) {
      this.blockBottle.textContent = bottle + ': ' + this._formatPrice(bottleCost) + suffix;
    }
  }
  _injectPropertyInputs() {
    const propDefs = [
      { name: "properties[SKU]", cls: 'dh-prop-sku' },
      { name: "properties[Volume]", cls: 'dh-prop-volume' },
      { name: "properties[Price]", cls: 'dh-prop-price' },
      { name: "properties[Bottle]", cls: 'dh-prop-tare' },
    ];

    propDefs.forEach(({ name, cls }) => {
      if (this.productForm.querySelector('input.' + cls)) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.className = cls;
      input.value = '';
      this.productForm.appendChild(input);
    });

    this.propSku = this.productForm.querySelector('.dh-prop-sku');
    this.propVolume = this.productForm.querySelector('.dh-prop-volume');
    this.propPrice = this.productForm.querySelector('.dh-prop-price');
    this.propTare = this.productForm.querySelector('.dh-prop-tare');
  }

  _volumesWithAtomizers() {
    const volumes = (this.catalog && this.catalog.volumes) || {};
    const pills = Array.from(this.querySelectorAll('.dh-dispense__pill'));
    return Object.keys(volumes)
      .map((k) => volumes[k])
      .filter((b) => {
        const vol = parseInt(b.volume, 10);
        if (!ATOMIZER_VOLUMES.has(vol)) return false;
        const pill = pills.find((p) => parseInt(p.dataset.volume, 10) === vol);
        if (!pill) return false;
        const qty = parseInt(pill.dataset.qty, 10);
        if (!Number.isNaN(qty) && qty <= 0) return false;
        return this._vesselsFor(vol).some((v) => v.kind === 'atomizer');
      })
      .sort((a, b) => a.volume - b.volume);
  }

  _vesselsFor(volume) {
    const bucket = ((this.catalog && this.catalog.volumes) || {})[String(volume)];
    const list = (bucket && bucket.vessels) || [];
    return list.filter((v) => v.stock == null || Number(v.stock) > 0);
  }

  _defaultVessel(volume) {
    const list = this._vesselsFor(volume);
    const flask = list.find((v) => v.kind === 'flask');
    if (flask) return flask;
    return list.slice().sort((a, b) => a.price - b.price)[0] || null;
  }


  _flaskButtonHtml() {
    const active = this.querySelector('.dh-dispense__pill--active');
    const volume = active ? parseInt(active.dataset.volume, 10) : 0;
    const flask = this._vesselsFor(volume).find((v) => v.kind === 'flask');
    if (!flask) return '';
    return (
      '<div class="dh-dispense__flask-line">' +
        '<button type="button" class="dh-dispense__flask"' +
        ' data-code="' + this._esc(flask.code) + '"' +
        ' data-volume="' + volume + '">' +
        this._esc(flask.name) +
        '</button>' +
      '</div>'
    );
  }

  _renderVesselRows() {
    if (!this.vesselsRoot || !this.vesselsEnabled) return;
    const rows = this._volumesWithAtomizers();
    const flaskHtml = this._flaskButtonHtml();
    if (!rows.length && !flaskHtml) {
      this.vesselsRoot.hidden = true;
      this.vesselsRoot.innerHTML = '';
      return;
    }
    this.vesselsRoot.hidden = false;
    const unit = this.dataset.volUnit || 'ml';
    const atomA = this.dataset.atomA || 'premium';
    const atomB = this.dataset.atomB || 'atomizer';
    const cells = rows.map((row) => {
      const atoms = this._vesselsFor(row.volume).filter((v) => v.kind === 'atomizer');
      const swatches = atoms.map((v) => this._swatchHtml(v, row.volume)).join('');
      return (
        '<div class="dh-dispense__vessel-row" data-volume="' + row.volume + '">' +
          '<div class="dh-dispense__swatches">' + swatches + '</div>' +
          '<div class="dh-dispense__vessel-label">' +
            '<strong>' + row.volume + ' ' + unit + '</strong>' +
            '<span>' + atomA + '<br>' + atomB + '</span>' +
          '</div>' +
        '</div>'
      );
    }).join('');
    this.vesselsRoot.innerHTML = flaskHtml + (cells
      ? '<div class="dh-dispense__vessel-line">' + cells + '</div>'
      : '');

    this.vesselsRoot.querySelectorAll('[data-code]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const vol = parseInt(btn.getAttribute('data-volume'), 10);
        const code = btn.getAttribute('data-code');
        const vessel = this._vesselsFor(vol).find((v) => v.code === code);
        const pill = this._pillForVolume(vol);
        if (!pill || !vessel) return;
        this.selectedVessel = vessel;
        this._selectPill(pill, { keepVessel: true });
      });
    });
  }

  _swatchHtml(v, volume) {
    const img = v.image && v.image.indexOf('no-photo') === -1 ? v.image : '';
    const style = img
      ? 'background-image:url(' + img + ')'
      : 'background-color:' + (v.color || '#888');
    const extra = (!img && (v.color === '#FFFFFF' || v.color === '#ffffff'))
      ? ' dh-dispense__swatch--light'
      : '';
    return (
      '<button type="button" class="dh-dispense__swatch' + extra + '"' +
      ' data-code="' + this._esc(v.code) + '"' +
      ' data-volume="' + volume + '"' +
      ' style="' + style + '"' +
      ' aria-label="' + this._esc(v.name) + '"' +
      ' title="' + this._esc(v.name) + '"></button>'
    );
  }

  _esc(s) {
    return String(s || '').replace(/[&<>"']/g, (ch) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[ch]));
  }

  _pillForVolume(volume) {
    return Array.from(this.querySelectorAll('.dh-dispense__pill')).find(
      (p) => parseInt(p.dataset.volume, 10) === volume
    );
  }

  _bindPills() {
    this.pills.forEach((pill) => {
      pill.addEventListener('click', (e) => {
        e.preventDefault();
        this.selectedVessel = this.vesselsEnabled
          ? this._defaultVessel(parseInt(pill.dataset.volume, 10))
          : null;
        this._selectPill(pill, { keepVessel: true });
      });
    });
  }

  _bindVariantSync() {
    if (!this.variantSelect) return;
    this.variantSelect.addEventListener('change', () => {
      if (this._syncing) return;
      const vid = String(this.variantSelect.value);
      const pill = Array.from(this.pills).find((p) => String(p.dataset.variantId) === vid);
      if (pill && !pill.classList.contains('dh-dispense__pill--active')) {
        this._syncing = true;
        this.selectedVessel = this.vesselsEnabled
          ? this._defaultVessel(parseInt(pill.dataset.volume, 10))
          : null;
        this._selectPill(pill, { silent: true, keepVessel: true });
        this._syncing = false;
      }
    });
  }

  _bindStickyAtc() {
    document.addEventListener('click', (e) => {
      const stickyBtn = e.target.closest('[data-sticky-addtocart] [data-action-atc]');
      if (!stickyBtn) return;
      if (stickyBtn.hasAttribute('data-action-delay') && window.innerWidth <= 767) return;

      const mainBtn = this.productForm.querySelector('[data-atc-form]');
      if (!mainBtn) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const stickyQty = document.querySelector('[data-sticky-addtocart] [data-quantity-value]');
      const mainQty = this.productForm.querySelector('[data-quantity-value], input[name="quantity"]');
      if (stickyQty && mainQty) mainQty.value = stickyQty.value;

      mainBtn.click();
    }, true);
  }

  _bindAtcIntercept() {
    if (!this.vesselsEnabled) return;
    const intercept = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      this._addTwoLines();
    };
    this.productForm.addEventListener('submit', (e) => {
      const pill = this.querySelector('.dh-dispense__pill--active');
      const volume = pill ? parseInt(pill.dataset.volume, 10) : 0;
      const vessel = this.selectedVessel || this._defaultVessel(volume);
      if (!vessel || !vessel.variant_id) return;
      intercept(e);
    }, true);
    this.productForm.querySelectorAll('[data-atc-form], button[name="add"], [type="submit"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const pill = this.querySelector('.dh-dispense__pill--active');
        const volume = pill ? parseInt(pill.dataset.volume, 10) : 0;
        const vessel = this.selectedVessel || this._defaultVessel(volume);
        if (!vessel || !vessel.variant_id) return;
        intercept(e);
      }, true);
    });
  }

  _markVesselButtons(volume) {
    if (!this.vesselsRoot) return;
    const code = this.selectedVessel && this.selectedVessel.code;
    this.vesselsRoot.querySelectorAll('[data-code]').forEach((btn) => {
      const on = btn.getAttribute('data-volume') === String(volume)
        && btn.getAttribute('data-code') === code;
      btn.classList.toggle('is-active', on);
    });
  }

  _selectPill(pill, opts) {
    opts = opts || {};
    this.pills.forEach((p) => p.classList.remove('dh-dispense__pill--active'));
    pill.classList.add('dh-dispense__pill--active');

    const volume = parseInt(pill.dataset.volume, 10);
    const variantId = pill.dataset.variantId;
    if (!opts.keepVessel) {
      this.selectedVessel = this.vesselsEnabled ? this._defaultVessel(volume) : null;
    } else if (this.vesselsEnabled && this.selectedVessel && this.selectedVessel.volume !== volume) {
      const match = this._vesselsFor(volume).find((v) => v.code === this.selectedVessel.code);
      this.selectedVessel = match || this._defaultVessel(volume);
    } else if (this.vesselsEnabled && !this.selectedVessel) {
      this.selectedVessel = this._defaultVessel(volume);
    }

    const bottleCost = this.selectedVessel
      ? Number(this.selectedVessel.price) || 0
      : (BOTTLE_PRICES[volume] || 35);
    const variantPrice = parseFloat(pill.dataset.price);
    const liquidCost = (!Number.isNaN(variantPrice) && variantPrice > 0)
      ? variantPrice
      : this.pricePerMl * volume;
    const total = liquidCost + bottleCost;
    const unitPrice = this._unitPrice(pill, volume);
    const sku = pill.dataset.sku || '';
    const baseSku = sku.replace(/-\d+m[lл]$/i, '');

    let volumeText = volume + ' ' + (this.dataset.volUnit || 'ml');
    if (this.selectedVessel && (this.selectedVessel.kind === 'atomizer' || this.selectedVessel.kind === 'flask')) {
      volumeText += ' \u00b7 ' + (this.selectedVessel.label || this.selectedVessel.name);
    }
    if (this.currentVolumeEl) this.currentVolumeEl.textContent = volumeText;

    this._updatePriceBlock(volume, liquidCost, bottleCost, unitPrice);
    this._renderVesselRows();
    this._markVesselButtons(volume);

    if (this.propSku) this.propSku.value = baseSku;
    if (this.propVolume) this.propVolume.value = volume + ' ml (' + volume + ' pcs)';
    if (this.propPrice) this.propPrice.value = this._formatPrice(total) + ' \u20ac';
    if (this.propTare) {
      this.propTare.value = this.selectedVessel
        ? (this.selectedVessel.name + ' ' + this._formatPrice(bottleCost) + ' \u20ac')
        : (this._formatPrice(bottleCost) + ' \u20ac');
    }

    if (!this.vesselsEnabled) {
      const qtyInput = this.productForm.querySelector('[data-quantity-value], input[name="quantity"]');
      if (qtyInput) qtyInput.value = volume;
    }

    this._overrideKallesPrice(total);

    if (this.variantSelect && variantId && !opts.silent) {
      this.variantSelect.value = variantId;
      this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this._updateSku(pill);
    window.setTimeout(() => {
      this._updateSku(pill);
      this._overrideKallesPrice(total);
    }, 60);
    window.setTimeout(() => this._overrideKallesPrice(total), 200);
  }

  _flaskQty() {
    const qtyInput = this.productForm.querySelector('[data-quantity-value], input[name="quantity"]');
    const n = parseInt(qtyInput && qtyInput.value, 10);
    return n > 0 ? n : 1;
  }

  _addTwoLines() {
    const pill = this.querySelector('.dh-dispense__pill--active');
    if (!pill) return;
    const volume = parseInt(pill.dataset.volume, 10);
    const perfumeId = parseInt(pill.dataset.variantId, 10);
    const vessel = this.selectedVessel || this._defaultVessel(volume);
    if (!vessel || !vessel.variant_id) {
      window.alert('Choose a flask or atomizer');
      return;
    }
    const qty = this._flaskQty();
    const sku = pill.dataset.sku || '';
    const baseSku = sku.replace(/-\d+m[lл]$/i, '');
    const bottleCost = Number(vessel.price) || 0;
    const variantPrice = parseFloat(pill.dataset.price);
    const liquidCost = (!Number.isNaN(variantPrice) && variantPrice > 0)
      ? variantPrice
      : this.pricePerMl * volume;
    const total = liquidCost + bottleCost;

    const kitId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    const items = [
      {
        id: vessel.variant_id,
        quantity: qty,
        properties: {
          _dh_vessel: '1',
          _dh_parent: baseSku,
          _dh_kit: kitId,
        },
      },
      {
        id: perfumeId,
        quantity: qty,
        properties: {
          "SKU": baseSku,
          "Volume": volume + ' ml (' + volume + ' pcs)',
          "Price": this._formatPrice(total) + ' €',
          "Bottle": vessel.name,
          _dh_kit: kitId,
        },
      },
    ];

    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          const msg = (data && (data.description || data.message)) || 'Could not add to cart';
          window.alert(msg);
          return;
        }
        window.location.href = root + 'cart';
      })
      .catch(() => window.alert('Could not add to cart'));
  }

  _refreshMiniCart() {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    Promise.all([
      fetch(root + '?section_id=mini_cart').then((r) => r.text()),
      fetch(root + 'cart.js').then((r) => r.json()),
    ]).then(([html, cart]) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById('t4s-mini_cart');
      const cur = document.getElementById('t4s-mini_cart');
      if (fresh && cur) cur.outerHTML = fresh.outerHTML;
      const count = (cart && cart.item_count) || 0;
      document.documentElement.className = document.documentElement.className.replace(
        /t4s-cart-count-\d+/,
        't4s-cart-count-' + count
      );
      document.querySelectorAll('[data-cart-count], .t4s_crcount, .t4s-count').forEach((el) => {
        if (el.hasAttribute('data-cart-count') || /^\d+$/.test((el.textContent || '').trim())) {
          el.textContent = String(count);
        }
      });
      const drawer = document.getElementById('t4s-mini_cart');
      if (drawer) {
        drawer.classList.remove('t4s-dn');
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('t4s-drawer-opening');
        document.documentElement.classList.add('t4s-drawer-opening');
      } else {
        const trigger = document.querySelector('a[data-drawer-options*="t4s-mini_cart"]');
        if (trigger) trigger.click();
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: cart }));
    }).catch(() => {
      window.location.href = root + 'cart';
    });
  }

  _updateSku(pill) {
    const sku = pill.dataset.sku;
    if (!sku || !this.skuEls || !this.skuEls.length) return;
    const baseSku = sku.replace(/-\d+m[lл]$/i, '');
    this.skuEls.forEach((el) => { el.textContent = baseSku; });
  }

  _formatPrice(amount) {
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  _formatUnit(amount) {
    const rounded = Math.round(amount * 100) / 100;
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(rounded);
  }
}
if (!customElements.get('dh-dispense-selector')) {
  customElements.define('dh-dispense-selector', DhDispenseSelector);
}
